import { Link } from 'react-router-dom';
import { trustedPartnerDetailPath } from '@/lib/constants/routes';
import type { TrustedPartnerSummaryDto } from '@/lib/types/events.types';
import { EVENTS_STRINGS } from '../constants/events-strings';

const S = EVENTS_STRINGS.DETAIL;

interface EventTrustedPartnerProps {
  /** The resolved partner. The caller renders nothing when the event has none. */
  partner: TrustedPartnerSummaryDto;
  /** Whether a session is active - decides whether the logo links through. */
  isAuthenticated: boolean;
}

/**
 * The Trusted Partner credited on an event detail page (FR-281, MYN-184).
 *
 * Logo only: the client ruled out showing the partner's name beside it, so the
 * name is carried as alt text rather than rendered. It is sized to read as a
 * credit rather than a thumbnail (A-334).
 *
 * **The link is the client's decision, not the server's.** `GET /events/:id` is
 * public and returns one payload to members and logged-out visitors alike, so
 * the summary carries no profile path and this component decides: a signed-in
 * member gets a link to the partner's profile, a logged-out visitor gets the
 * bare image. `/trusted-partners/:entityId` is behind auth, so linking a
 * logged-out visitor there would only bounce them to the login screen
 * (design §51, A-333).
 */
export function EventTrustedPartner({ partner, isAuthenticated }: EventTrustedPartnerProps) {
  const logo = (
    <img
      src={partner.logoUrl}
      alt={partner.name}
      className="max-h-24 w-auto max-w-full object-contain sm:max-h-28"
    />
  );

  return (
    <div>
      <h3 className="text-[0.875rem] font-medium text-subtext">{S.TRUSTED_PARTNER_HEADING}</h3>
      <div className="mt-3">
        {isAuthenticated ? (
          <Link
            to={trustedPartnerDetailPath(partner.entityId)}
            className="inline-block cursor-pointer rounded-md transition-opacity hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            {logo}
          </Link>
        ) : (
          logo
        )}
      </div>
    </div>
  );
}
